import { http, LayoutActivityType } from '@Techedge/digitalportal-shared'
import { AxiosPromise } from 'axios'
import _ from 'lodash'
import { DistributionFiltersApi } from '@models/distribution'

const buildBodyForCreateAndUpdateFilter = (filter: any) => {
    const timeFrameWithoutNilValues = _.omitBy(filter.categories.timeFrame, _.isNil)

    const body = {
        ...filter,
        categories: {
            ...filter.categories,
            timeFrame: timeFrameWithoutNilValues,
        },
    }
    delete body.userId
    delete body._id
    delete body.isDefault
    delete body.isApply
    delete body.lastupdate

    if (!body.categories.project.projectTitle) {
        delete body.categories.project.projectTitle
    }

    //Handle old filters
    delete body.isFavorite

    return body
}

export class FiltersService {
    static BASE_URL: string = process.env.REACT_APP_BASE_URL_BE
    static FILTERS_URL: string = FiltersService.BASE_URL + `/filtersapi`

    static SEARCH_API_URL: string = FiltersService.BASE_URL + `/searchapi`
    static SEARCH_API_URL_ACTIVITY_LIST: string =
        FiltersService.BASE_URL + `/searchapi/activitylist`

    static headersObj = {
        headers: {
            'Accept-Language': '',
        },
        timeout: 180000,
    }

    static GetRecipients = (): any => {
        return http.get<any>(`${FiltersService.FILTERS_URL}/recipients`, FiltersService.headersObj)
    }

    static GetDepartments = (): any => {
        return http.get<any>(`${FiltersService.FILTERS_URL}/departments`, FiltersService.headersObj)
    }

    static GetVisibilities = (): any => {
        return http.get<any>(
            `${FiltersService.FILTERS_URL}/visibilities`,
            FiltersService.headersObj
        )
    }

    static GetSubTypes = (type: LayoutActivityType): any => {
        return http.get<any>(`${FiltersService.FILTERS_URL}/subtypes`, {
            ...FiltersService.headersObj,
            params: { type },
        })
    }

    static GetMacroareas = (banner?: string[]): any => {
        const params = banner ? `?banner=${banner.toString()}` : ''
        return http.get<any>(`${FiltersService.FILTERS_URL}/macroareas${params}`, {
            ...FiltersService.headersObj,
        })
    }

    static CreateDistributionList = (): any => {
        return http.post<any>(
            `${FiltersService.FILTERS_URL}/distributionlists`,
            {},
            FiltersService.headersObj
        )
    }

    static DeleteDistributionList = (): any => {
        return http.delete<any>(
            `${FiltersService.FILTERS_URL}/distributionlists/{distributionListId}`,
            FiltersService.headersObj
        )
    }

    // static GetFilterList = (filterId?: string) => {
    //     return http.get<any>(
    //         `${FiltersService.FILTERS_URL}/filters${filterId ? `?id=${filterId}` : ''}`,
    //         FiltersService.headersObj
    //     )
    // }

    static CreateFilter = (filter?: any): any => {
        const body = buildBodyForCreateAndUpdateFilter(filter)

        return http.post<any>(
            `${FiltersService.FILTERS_URL}/filters`,
            body,
            FiltersService.headersObj
        )
    }

    static UpdateFilter = (filter: any): any => {
        const body = buildBodyForCreateAndUpdateFilter(filter)
        const filterId = filter?._id
        return http.put<any>(
            `${FiltersService.FILTERS_URL}/filters/${filterId}`,
            body,
            FiltersService.headersObj
        )
    }

    static DeleteFilter = (filterId: string): any => {
        return http.delete<any>(
            `${FiltersService.FILTERS_URL}/filters/${filterId}`,
            FiltersService.headersObj
        )
    }

    static SetFavouriteFilter = (filterId: string): any => {
        return http.put<any>(
            `${FiltersService.FILTERS_URL}/filters/set_default/${filterId}`,
            {},
            FiltersService.headersObj
        )
    }

    static GetStoreList = ({
        mode,
        coneOfVision,
        page,
        pageSize,
        sort,
        tableFilters,
        groupedFields,
        controller,
    }: {
        mode: string
        coneOfVision?: boolean
        page?: number
        pageSize?: number
        sort?: { field?: string; type?: 'asc' | 'desc' }
        tableFilters?: DistributionFiltersApi
        groupedFields?: string
        controller?: AbortController
    }): any => {
        const { storeId, storeName, search, ...body } = tableFilters || {}
        return http.put<any>(
            `${FiltersService.FILTERS_URL}/stores`,
            { ...body },
            {
                ...FiltersService.headersObj,
                ...(controller ? { signal: controller?.signal } : {}),
                params: {
                    mode,
                    coneOfVision: coneOfVision || false,
                    groupedFields: groupedFields ? groupedFields : 'banner',
                    page: page || null,
                    pageSize: pageSize || null,
                    storeId,
                    storeName,
                    search,
                    sort: JSON.stringify(sort || {}),
                },
            }
        )
    }

    static GetAssignees = (
        storeId?: string,
        profileList?: string[],
        role?: { isApproval: boolean; code: string; search?: string }
    ): any => {
        // smartlyRoles?: string[] | string; -> ruoli esecutore
        // r360Roles?: string[] | string; -> ruoli approvatore
        let queryString = ''
        if (role?.code) {
            queryString = `?${storeId ? `storeId=${storeId}&` : ''}${
                role.isApproval ? 'r360Roles' : 'smartlyRoles'
            }=${role.code}${role.search ? `&search=${role.search}` : ''}`
        }
        return http.get<any>(
            `${FiltersService.FILTERS_URL}/assignees${queryString}${
                profileList ? `&profileList=[${profileList.toString()}]` : ''
            }`,
            FiltersService.headersObj
        )
    }

    static GetDistributionLists = (): AxiosPromise => {
        return http.get<any>(
            `${FiltersService.FILTERS_URL}/distributionlists`,
            FiltersService.headersObj
        )
    }

    static SaveDistributionList = (name: string, storesId: any[]): any => {
        return http.post<any>(
            `${FiltersService.FILTERS_URL}/distributionlists`,
            { name, storesId },
            FiltersService.headersObj
        )
    }

    static GetActivityFiltersOptions = () => {
        return http.get<any>(
            `${FiltersService.SEARCH_API_URL_ACTIVITY_LIST}/filters?applyUserFilter=false`,
            {
                ...FiltersService.headersObj,
            }
        )
    }
    static DoApplyFilter = (filterId: string): any => {
        return http.put<any>(
            `${FiltersService.FILTERS_URL}/filters/apply/${filterId}`,
            {},
            FiltersService.headersObj
        )
    }
    static GetActivityTypes = (params?: {
        projectTypesNames?: string[]
        feCheck?: boolean
        newVersion?: boolean
    }) => {
        const urlParams = new URLSearchParams()
        ;(params?.projectTypesNames || []).map((p) => urlParams.append('projectTypeNames', p))
        if (params?.hasOwnProperty('feCheck'))
            urlParams.append('feCheck', params?.feCheck?.toString())
        if (params?.newVersion)
            urlParams.append('newVersion', params?.newVersion.toString())
        const queryString = !!urlParams ? `?${urlParams.toString()}` : urlParams.toString()
        return http.get<any>(`${FiltersService.FILTERS_URL}/activitytypes${queryString}`, {
            ...FiltersService.headersObj,
        })
    }

    static GetReportTiming = (): AxiosPromise => {
        return http.get<any>(
            `${FiltersService.FILTERS_URL}/report/timing`,
            FiltersService.headersObj
        )
    }

    static DownloadStoreList = (
        mode: string,
        coneOfVision: boolean,
        page?: number,
        pageSize?: number,
        sort?: { field?: string; type?: 'asc' | 'desc' },
        tableFilters?: {
            storeName?: string
            banner?: string
            storeId?: string
            country?: string
            macroregion?: string
        },
        storeIdList?: string[],
        groupedFields?: string
    ): any => {
        return http.put<any>(
            `${FiltersService.SEARCH_API_URL}/export/stores`,
            {
                storeIdList,
                countryList: tableFilters?.country,
                macroregionList: tableFilters?.macroregion,
            },
            {
                ...FiltersService.headersObj,
                params: {
                    mode,
                    coneOfVision,
                    groupedFields: groupedFields ? groupedFields : 'banner',
                    page: page ? page : null,
                    pageSize: pageSize ? pageSize : null,
                    sort: JSON.stringify(sort),
                    storeName: tableFilters?.storeName,
                    banner: tableFilters?.banner,
                    storeId: tableFilters?.storeId,
                },
            }
        )
    }

    static GetMacroareaList = (banner: string) => {
        return http.get<any>(
            `${FiltersService.FILTERS_URL}/macroareas?banner=${banner}&flat=true`,
            {
                ...FiltersService.headersObj,
            }
        )
    }

    static GetPictureStatusList = () => {
        return http.get<any>(`${FiltersService.FILTERS_URL}/picturestatus`, {
            ...FiltersService.headersObj,
        })
    }

    static GetPhotoTagList = (banner?: string[]) => {
        const params = banner ? `?${banner.map((b) => 'banner=' + b).join('&')}` : ''
        // const params = '?banner=' + banner[0]
        return http.get<any>(`${FiltersService.FILTERS_URL}/tags${params}`, {
            ...FiltersService.headersObj,
        })
    }

    static GetCancelVisitReasons = () => {
        return http.get(`${FiltersService.FILTERS_URL}/reasons`, {
            ...FiltersService.headersObj,
        })
    }
}
